import type { ReactNode } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/admin/coaching/coaching-dashboard/shared/tooltip'
import type { RankingChipTone, ReviewRanking } from './contracts'

/**
 * Ranking banner (spec §8.4) — purple strip at the top of an expanded review:
 * "✦ #{position} in queue" pill + one-line reason + reason chips. Chip hover
 * detail uses `shared/tooltip.tsx` (never `title=`, spec §13.6; wrapper adds 2s
 * open delay) — the chips themselves are static/informational, not comment targets
 * (spec §8.4 only calls out pacing/post-mortem tiles and per-type atoms as targets).
 *
 * Content-gated (spec D16): a submission carries a ranking only when something
 * actually ranked it (`snapshotRanking`), and the strip renders nothing at all
 * otherwise — no pill, no border, no gap. The gate lives here rather than at
 * the four bodies that mount it so that no call site can forget it, and so the
 * banner returns the day a real snapshot is written.
 */

const TONE_META: Record<RankingChipTone, { color: string; bg: string }> = {
  amber: {
    color: 'var(--cd-amber)',
    bg: 'color-mix(in oklab, var(--cd-amber) 12%, transparent)',
  },
  red: {
    color: 'var(--cd-red)',
    bg: 'color-mix(in oklab, var(--cd-red) 12%, transparent)',
  },
  blue: {
    color: 'var(--cd-blue)',
    bg: 'color-mix(in oklab, var(--cd-blue) 12%, transparent)',
  },
}

export function RankingBanner(props: {
  ranking: ReviewRanking | undefined
}): ReactNode {
  const { ranking } = props
  if (ranking === undefined) return null

  return (
    <div
      className="flex flex-wrap items-center gap-2.5 rounded-[10px] border px-3.5 py-2.5"
      style={{
        background: 'color-mix(in oklab, var(--cd-purple) 6%, transparent)',
        borderColor: 'color-mix(in oklab, var(--cd-purple) 22%, transparent)',
      }}
    >
      <span
        className="shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-bold text-(--cd-purple)"
        style={{
          background: 'color-mix(in oklab, var(--cd-purple) 14%, transparent)',
        }}
      >
        ✦ #{ranking.position} in queue
      </span>
      <span className="text-[12.5px] leading-[1.5] text-(--cd-text-2)">
        {ranking.reason}
      </span>
      {/* Skipped rather than rendered empty: an empty chip row is still a flex
          item of the gap-2.5 line above it, so it reads as dead space after
          the reason. A ranking with no chips is reachable — the snapshot's
          position and reason are what make a ranking, and a signal it cannot
          read in full is dropped. */}
      {ranking.chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {ranking.chips.map((chip, index) => {
            const tone = TONE_META[chip.tone]
            return (
              // Content *and* position: the chips are read off an untyped
              // snapshot that nothing stops from carrying two signals under
              // one label, or the same signal twice. The row is rebuilt whole
              // from that snapshot and never reordered or spliced, so the
              // position is stable for as long as the chips are.
              <Tooltip key={`${index}:${chip.tone}:${chip.icon}:${chip.label}`}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex shrink-0 cursor-default items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
                    style={{ color: tone.color, background: tone.bg }}
                  >
                    {chip.icon} {chip.label}
                  </button>
                </TooltipTrigger>
                <TooltipContent>{chip.detail}</TooltipContent>
              </Tooltip>
            )
          })}
        </div>
      )}
    </div>
  )
}
